import { type ApolloError, useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import type { SupervisorValue, ChiefSupervisorFromValues } from './types';
import { type SelectOption } from '../../../../../components';

export type GetChiefSupervisor = {
  chiefSupervisor: ChiefSupervisorFromValues;
  coSupervisors: SupervisorValue[];
  departmentOptions: SelectOption[];
  loading: boolean;
  error?: ApolloError;
  refetch: () => void;
};

export const useGetChiefSupervisors = (
  id: string,
  skip?: boolean,
): GetChiefSupervisor => {
  const { data, loading, error, refetch } = useQuery(
    gql`
      query ($id: ID!) {
        programmeChoiceMetadata {
          departments {
            value
            label
          }
        }

        nomination(id: $id) {
          supervisors {
            type
            netId
            institution
            title
            jobTitle
            givenName
            surname
            department
            email
            previousSuccessfulSupervision
            isDrcChair
            isDoRPgS
          }
        }
      }
    `,
    {
      variables: { id },
      skip: !id || skip === true,
    },
  );

  const supervisorTeamMemberData: SupervisorValue[] = (
    data?.nomination?.supervisors ?? []
  )?.map((supervisor: SupervisorValue) => {
    const {
      type = '',
      netId,
      institution = '',
      title = '',
      jobTitle,
      givenName,
      surname,
      department,
      email,
      previousSuccessfulSupervision,
      isDrcChair,
      isDoRPgS = false,
    } = supervisor ?? {};

    return {
      type,
      netId,
      institution,
      title,
      jobTitle,
      givenName,
      surname,
      department,
      email,
      previousSuccessfulSupervision,
      isDrcChair,
      isDoRPgS,
    };
  });
  const firstSupervisor = supervisorTeamMemberData?.shift();
  const chiefSupervisor: ChiefSupervisorFromValues = {
    netId: firstSupervisor?.netId ?? '',
    institution: firstSupervisor?.institution ?? '',
    title: firstSupervisor?.title ?? '',
    jobTitle: firstSupervisor?.jobTitle ?? '',
    givenName: firstSupervisor?.givenName ?? '',
    surname: firstSupervisor?.surname ?? '',
    department: firstSupervisor?.department ?? '',
    email: firstSupervisor?.email ?? '',
    previousSuccessfulSupervision:
      firstSupervisor?.previousSuccessfulSupervision ?? false,
    isDrcChair: firstSupervisor?.isDrcChair ?? false,
    isDoRPgS: firstSupervisor?.isDoRPgS ?? false,
  };
  const coSupervisors: SupervisorValue[] = supervisorTeamMemberData ?? [];

  // programmeChoiceMetadata, department
  const departmentOptions: SelectOption[] = (
    (data?.programmeChoiceMetadata?.departments as any[]) ?? []
  ).map(department => ({
    value: department.value,
    label: department.label,
  }));

  return {
    chiefSupervisor,
    coSupervisors,
    departmentOptions,
    refetch,
    loading,
    error,
  };
};
